import {
  AnimationCharacter,
  Comment,
  Effect,
  Layer,
  Scene,
  TextProps,
} from '../types/typing.ts';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './utils/authTokenUtils.ts';
import { ApiError } from './utils/apiError';
import type { GaAnalyticsPayload } from './ga4Client';

export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface Video {
  _id: string;
  ownedBy: User | string; // Owner is a full User object
  title: string;
  description: string;
  thumbnailUrl: string;
  backgroundImage: string;
  scenes: Scene[];
  audio: [{ id: string; url: string }];
  animations: AnimationCharacter[];
  comments: Comment[];
  effects: Effect[];
  texts: TextProps[];
  layers: Layer[];
  timeline: any;
  duration?: number; // Optional if missing in response
  sas?: {
    // SAS query string used to build playable URLs from relative blob paths
    token?: string;
    // SAS token expiry, as UTC epoch milliseconds
    expiredAt?: number;
  };
  sharedWith?: User[] | string[]; // Array of User objects
  createdAt: string;
  updatedAt: string;
}

export interface VideoFormData {
  title: string;
  description: string;
  thumbnailUrl: string;
  duration: number;
}

export const videoService = {
  /**
   * Create a new video
   */
  createVideo: async (
    videoData: VideoFormData,
    analytics?: GaAnalyticsPayload
  ): Promise<Video> => {
    const response = await fetch(`${API_BASE_URL}/video/createNew`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ ...videoData, ...(analytics ? { analytics } : {}) }),
    });

    if (!response.ok) throw new ApiError('Failed to create video', response.status);
    return response.json();
  },

  /**
   * Report a client-completed export (canvas recording fallback).
   * Fire-and-forget; analytics must never block the export.
   */
  recordVideoExport: async (
    videoId: string,
    exportId: string,
    exportFormat: 'mp4' | 'webm',
    analytics?: GaAnalyticsPayload
  ): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/video/exportComplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        videoId,
        exportId,
        exportFormat,
        ...(analytics ? { analytics } : {}),
      }),
    });
    if (!response.ok) {
      throw new ApiError('Failed to record video export', response.status);
    }
  },

  /**
   * Get video by ID
   */
  getVideoById: async (id: string): Promise<Video> => {
    const response = await fetch(`${API_BASE_URL}/video/getVideo/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) throw new ApiError('Failed to fetch video', response.status);
    return response.json();
  },

  /**
   * Get all videos owned by the current user
   */
  getMyVideos: async (): Promise<Video[]> => {
    const response = await fetch(`${API_BASE_URL}/video/getVideosOwnedByMe`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch my videos', response.status);
    }
    return response.json();
  },

  patchVideo: async (videoId: string, data: any): Promise<Video> => {
    const response = await fetch(
      `${API_BASE_URL}/video/updateVideo/${videoId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new ApiError('Failed to update video', response.status);
    return response.json();
  },

  /**
   * Delete a video owned by the current user
   */
  deleteVideo: async (videoId: string): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/video/deleteVideo/${videoId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new ApiError('Failed to delete video', response.status);
    }
  },

  /**
   * Get all videos shared with the current user
   */
  getSharedVideos: async (): Promise<Video[]> => {
    const response = await fetch(
      `${API_BASE_URL}/video/getVideosSharedWithMe`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new ApiError('Failed to fetch shared videos', response.status);
    }
    return response.json();
  },

  /**
   * Share video with a user
   */
  shareVideo: async (videoId: string, email: string): Promise<Video[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/video/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ videoId, email }),
      });

      if (!response.ok) throw new ApiError('Failed to share video', response.status);

      return await response.json();
    } catch (error) {
      console.error('Error in shareVideo:', error);
      throw error; // Rethrow for higher-level handling
    }
  },

  unshareVideo: async (videoId: string | undefined, email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/video/unshare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ videoId, email }),
      });

      if (!response.ok)
        throw new ApiError('Failed to unshare video', response.status);

      return await response.json();
    } catch (error) {
      console.error('Error in unshareVideo:', error);
      throw error; // Rethrow for higher-level handling
    }
  },

  /**
   * Upload a video file and return a simulated storage URL
   */
  uploadVideoFile: async (file: File): Promise<string> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const randomId = Math.random().toString(36).substring(2, 15);
        const extension = file.name.split('.').pop();
        resolve(`https://storage.pixle.pro/videos/${randomId}.${extension}`);
      }, 1000);
    });
  },

  /**
   * Upload a thumbnail image and return a simulated storage URL
   */
  /*uploadThumbnail: async (file: File): Promise<string> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const randomId = Math.random().toString(36).substring(2, 15);
                resolve(`https://storage.pixle.pro/thumbnails/${randomId}.jpg`);
            }, 1000);
        });
    },*/
};
